import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ZardDatePickerComponent } from '../../../app/shared/components/date-picker/date-picker.component';
import { SvzValidationMessageComponent } from '../../svz-validation-message/svz-validation-message.component';
import { SvzControl } from '../svz-control';

@Component({
  selector: 'svz-date-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-date-picker.component.html',
  providers: [DatePipe, { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SvzDatePickerComponent), multi: true }],
  standalone: true,
  imports: [ZardDatePickerComponent, SvzValidationMessageComponent],
})
export class SvzDatePickerComponent extends SvzControl {
  private readonly datePipe = inject(DatePipe);

  readonly label = input<string>('');

  readonly readonly = input<boolean>(false);

  readonly minDate = input<Date | null>(null, { alias: 'min' });

  readonly maxDate = input<Date | null>(null, { alias: 'max' });

  readonly format = input('dd/MM/yyyy', {
    transform: (value: string | null | undefined) => value || 'dd/MM/yyyy',
  });

  readonly inputId = input('');

  readonly showIcon = input<boolean>(true);

  readonly inputReadOnly = input<boolean>(false);

  readonly showValidation = input<boolean>(true);

  readonly dateFilter = input<((date: Date) => boolean) | null>(null);

  readonly fieldAppearance = input<'default' | 'design-system'>('default');

  protected readonly placeholderText = computed(() => (this.fieldAppearance() === 'design-system' ? this.format().toLowerCase() : this.format()));

  protected calendarValue: Date | null = null;

  override writeValue(value: unknown): void {
    super.writeValue(value);
    this.calendarValue = this.parseDateValue(value);
  }

  onDateSelected(value: Date | null): void {
    const dateFilter = this.dateFilter();
    if (value && dateFilter && !dateFilter(value)) {
      return;
    }

    this.calendarValue = value;
    const nextValue = value ? this.toStorageFormat(value) : null;
    this.control.setValue(nextValue, { emitEvent: false });
    this.value = nextValue;
    this.onChange(nextValue);
    this.onTouched();
  }

  protected get readonlyText(): string {
    if (!this.control?.value && !this.value) {
      return '';
    }

    const parsedDate = this.parseDateValue(this.control?.value ?? this.value);
    if (!parsedDate) {
      return String(this.control?.value ?? this.value ?? '');
    }

    return this.datePipe.transform(parsedDate, this.format()) ?? '';
  }

  private parseDateValue(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      const parsedDate = new Date(value);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
  }

  private toStorageFormat(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
